import dayjs from 'dayjs';

import {
    PayrollActivityPage,
    PayrollActivityRun,
    PayrollAlertList,
    PayrollFunding,
    PayrollOverviewCore,
    PayrollYtd,
} from './usePayrollOverview.interface';

import { MoneyOperationsBlockedByEnum } from 'services/payroll/api/generated/v1/model';
import type { PayScheduleList } from 'services/payroll/api/generated/v1/model';

/**
 * Fixtures for the Payroll Overview sections. Values mirror the Figma frame so the page can be
 * reviewed against the design, and they are what the MSW handlers serve — so the demo exercises
 * the real request layer rather than a second code path.
 *
 * Dates are derived from today rather than hardcoded: a fixed date drifts into the past and the
 * deadline silently renders as "Overdue", which is a different state from the one under review.
 */

const TODAY = dayjs();
const ISO_DATE = 'YYYY-MM-DD';

/** Month offset from the current month: -1 is last month, +1 is next month. */
const monthPeriod = (offset: number) => {
    const month = TODAY.add(offset, 'month');

    return {
        period_start_date: month.startOf('month').format(ISO_DATE),
        period_end_date: month.endOf('month').format(ISO_DATE),
    };
};

export const MOCK_OVERVIEW_CORE: PayrollOverviewCore = {
    next_run: {
        pay_schedule_uuid: 'b7f1b271-0000-4c3a-9b7e-1f2a3b4c5d61',
        payroll_uuid: '8f2a1c14-6d3b-4f8e-9a21-5b7c0e4d1f33',
        frequency: 'monthly',
        ...monthPeriod(0),
        // 4:00pm on the deadline day, not the day: the field is a moment, and a date-only
        // fixture is what let the UI drop the time unnoticed.
        run_payroll_deadline: TODAY.add(18, 'day').hour(16).minute(0).second(0).millisecond(0).toISOString(),
        check_date: TODAY.add(23, 'day').format(ISO_DATE),
        total_to_debit: '14166.24',
        employee_count: 1,
        includes_owner: true,
        debit_date: TODAY.add(21, 'day').format(ISO_DATE),
        status: 'draft',
        can_submit: true,
    },
    auto_payroll_enabled: false,
    can_skip_run: true,
    money_operations_blocked_by: [],
    blocking_alert_count: 0,
    reasonable_compensation: {
        owner: { full_name: 'Jon Kuminga', role: 'Owner', status: 'Active' },
        is_on_track: false,
        message: "You're off track: projected $96,000 of your $120,000 salary by Dec 31",
        verdict_unavailable_reason: null,
        projected_year_total: '96000.00',
        annual_salary: '120000.00',
        remaining_runs: 7,
        paid_to_date: '64000.00',
    },
    owner_paid_this_year: '64000.00',
    team: { w2_employee_count: 2, contractor_count: 2, pending_invitations: 1 },
    active_pay_schedule_count: 2,
    documents: [
        {
            document_uuid: 'd0c0d0c0-0001-4c3a-9b7e-1f2a3b4c5d61',
            document_type: 'ARTICLES_OF_ORGANIZATION',
            display_name: 'Articles of Incorporation & Bylaws',
        },
        {
            document_uuid: 'd0c0d0c0-0002-4c3a-9b7e-1f2a3b4c5d62',
            document_type: 'FORM_2553',
            display_name: 'IRS Form 2553',
        },
    ],
    as_of: TODAY.subtract(20, 'minute').toISOString(),
    degraded_sections: [],
};

/**
 * A member with payroll enabled but no schedule yet — the page must still render.
 *
 * @public Consumed by the MSW handlers and stories landing later in this stack.
 */
export const EMPTY_OVERVIEW_CORE: PayrollOverviewCore = {
    next_run: null,
    auto_payroll_enabled: false,
    can_skip_run: false,
    money_operations_blocked_by: [],
    blocking_alert_count: 0,
    reasonable_compensation: null,
    owner_paid_this_year: null,
    team: { w2_employee_count: 0, contractor_count: 0, pending_invitations: 0 },
    active_pay_schedule_count: 0,
    documents: [],
    as_of: null,
    degraded_sections: [],
};

export const MOCK_YTD: PayrollYtd = {
    year: TODAY.year(),
    salary_paid: '80183.22',
    contractor_pay: '16583.22',
    taxes_paid: '18321.00',
    benefits_paid: '16583.22',
    as_of: TODAY.subtract(5, 'minute').toISOString(),
};

export const MOCK_FUNDING: PayrollFunding = {
    name: 'Business premier',
    last_four: '3880',
    balance: '35000.24',
    balance_synced_at: TODAY.subtract(2, 'hour').toISOString(),
    connection: 'linked',
    account_type: 'checking',
    institution_name: 'Chase',
    institution_id: 'ins_56',
    plaid_account_id: 'demo-plaid-account-3880',
};

/**
 * Payroll debits an account the member has never connected here, so the balance is unknowable and
 * the debits never reach their books.
 *
 * @public Consumed by the stories and the MSW handlers.
 */
export const UNCONNECTED_FUNDING: PayrollFunding = {
    name: 'Business premier',
    last_four: '3880',
    balance: null,
    balance_synced_at: null,
    connection: 'not_connected',
    account_type: null,
    institution_name: null,
    institution_id: null,
    plaid_account_id: null,
};

/**
 * A member who has never run payroll — the tiles are genuinely zero, not a failed load.
 *
 * @public Consumed by the MSW handlers and stories landing later in this stack.
 */
export const EMPTY_YTD: PayrollYtd = {
    year: TODAY.year(),
    salary_paid: '0.00',
    contractor_pay: '0.00',
    taxes_paid: '0.00',
    benefits_paid: '0.00',
    as_of: null,
};

/** A run's scheduled pay day: the last day of its month, processed or not. */
const payDay = (offset: number) => TODAY.add(offset, 'month').endOf('month').format(ISO_DATE);

/** A processed run: ran a few days before the period ended, paid at month end. */
const processedDates = (offset: number) => {
    const month = TODAY.add(offset, 'month');

    return {
        date_ran: month.date(25).format(ISO_DATE),
        date_paid: month.endOf('month').format(ISO_DATE),
    };
};

export const MOCK_ACTIVITY: PayrollActivityPage = {
    count: 6,
    as_of: TODAY.subtract(1, 'hour').toISOString(),
    results: [
        {
            payroll_uuid: 'a1d4e2f0-0001-4c3a-9b7e-1f2a3b4c5d61',
            pay_schedule_uuid: null,
            ...monthPeriod(1),
            check_date: payDay(1),
            frequency_label: 'Monthly payroll',
            total_amount: '14166.24',
            date_ran: null,
            date_paid: null,
            display_status: 'UPCOMING',
            can_skip: true,
            can_cancel: false,
            period_conflict: false,
        },
        {
            payroll_uuid: 'a1d4e2f0-0002-4c3a-9b7e-1f2a3b4c5d62',
            pay_schedule_uuid: null,
            ...monthPeriod(0),
            check_date: payDay(0),
            frequency_label: 'Monthly payroll',
            total_amount: '14166.24',
            date_ran: null,
            date_paid: null,
            display_status: 'NEXT',
            can_skip: true,
            can_cancel: false,
            period_conflict: false,
        },
        {
            payroll_uuid: 'a1d4e2f0-0003-4c3a-9b7e-1f2a3b4c5d63',
            pay_schedule_uuid: null,
            ...monthPeriod(-1),
            check_date: payDay(-1),
            ...processedDates(-1),
            frequency_label: 'Monthly payroll',
            total_amount: '16234.24',
            display_status: 'COMPLETE',
            can_skip: false,
            can_cancel: false,
            period_conflict: false,
        },
        {
            payroll_uuid: 'a1d4e2f0-0004-4c3a-9b7e-1f2a3b4c5d64',
            pay_schedule_uuid: null,
            ...monthPeriod(-2),
            check_date: payDay(-2),
            ...processedDates(-2),
            frequency_label: 'Monthly payroll',
            total_amount: '14166.24',
            display_status: 'COMPLETE',
            can_skip: false,
            can_cancel: false,
            period_conflict: false,
        },
        {
            // 28% of processed runs are genuinely $0 — a zero here is a fact, not a failure.
            payroll_uuid: 'a1d4e2f0-0005-4c3a-9b7e-1f2a3b4c5d65',
            pay_schedule_uuid: null,
            ...monthPeriod(-3),
            check_date: payDay(-3),
            frequency_label: 'Monthly payroll',
            total_amount: '0.00',
            date_ran: null,
            date_paid: null,
            display_status: 'SKIPPED',
            can_skip: false,
            can_cancel: false,
            period_conflict: false,
        },
        {
            payroll_uuid: 'a1d4e2f0-0006-4c3a-9b7e-1f2a3b4c5d66',
            pay_schedule_uuid: null,
            ...monthPeriod(-4),
            check_date: payDay(-4),
            ...processedDates(-4),
            frequency_label: 'Monthly payroll',
            total_amount: '14166.24',
            display_status: 'COMPLETE',
            can_skip: false,
            can_cancel: false,
            period_conflict: false,
        },
    ],
};

/**
 * A blocked company. This is what the blocking-alerts design actually draws: the banner sits
 * *over* the next-run-with-auto-payroll card, so both facts are true of the same response at once.
 *
 * @public Consumed by the MSW handlers and the Overview stories.
 */
export const BLOCKED_OVERVIEW_CORE: PayrollOverviewCore = {
    ...MOCK_OVERVIEW_CORE,
    auto_payroll_enabled: true,
    blocking_alert_count: 1,
};

/**
 * A company with auto payroll on and nothing blocking it.
 *
 * @public Consumed by the MSW handlers and the Overview stories.
 */
export const AUTO_PAYROLL_OVERVIEW_CORE: PayrollOverviewCore = {
    ...MOCK_OVERVIEW_CORE,
    auto_payroll_enabled: true,
};

/**
 * A company Gusto has not approved yet, as the server reports it whatever the alert flags say: no
 * blocking alert, and the run and skip closed by the review alone.
 *
 * @public Consumed by the Overview tests and stories.
 */
export const IN_REVIEW_OVERVIEW_CORE: PayrollOverviewCore = {
    ...MOCK_OVERVIEW_CORE,
    next_run: MOCK_OVERVIEW_CORE.next_run && { ...MOCK_OVERVIEW_CORE.next_run, can_submit: false },
    can_skip_run: false,
    money_operations_blocked_by: [MoneyOperationsBlockedByEnum.provider_review_pending],
};

/** Every alert payload carries per-entity freshness; nothing in this ticket reads it. */
const ALERT_SYNC: PayrollAlertList['sync'] = [
    {
        entity_type: 'company',
        last_synced_at: TODAY.subtract(12, 'minute').toISOString(),
        last_event_at: TODAY.subtract(12, 'minute').toISOString(),
        records_seen: 1,
        records_written: 1,
        consecutive_failures: 0,
        is_stale: false,
    },
];

/**
 * One self-serve blocker with a single unblock option. The option's
 * label is what the banner's button reads, so the server names the action rather than the client
 * inventing one.
 *
 * @public Consumed by the MSW handlers and the Overview stories.
 */
export const MOCK_ALERTS: PayrollAlertList = {
    count: 1,
    next: null,
    previous: null,
    blocking_count: 1,
    results: [
        {
            alert_uuid: 'a1e70000-0001-4c3a-9b7e-1f2a3b4c5d61',
            code: 'INSUFFICIENT_FUNDS',
            severity: 'blocking',
            resolution: 'self_serve',
            title: 'Add funds to unblock payroll',
            message:
                "Your account may not cover this payroll. Add funds to the account we debit, or we won't be able to process the run.",
            source: 'sync',
            subject_type: 'company',
            subject_id: 'c0000000-0001-4c3a-9b7e-1f2a3b4c5d61',
            occurrence_count: 1,
            opened_at: TODAY.subtract(2, 'day').toISOString(),
            acknowledged_at: null,
            can_acknowledge: false,
            action_url: '',
            unblock_options: [
                {
                    key: 'visit_bank',
                    label: 'Visit bank',
                    description: 'Open your bank to move funds into the account we debit.',
                },
            ],
        },
    ],
    sync: ALERT_SYNC,
    as_of: TODAY.subtract(12, 'minute').toISOString(),
};

/**
 * A blocker that clears on its own. `resolution: 'wait'` is the server saying there is nothing to
 * press, so the banner renders no button — the field exists for exactly that decision.
 *
 * @public Consumed by the Overview stories.
 */
export const WAITING_ALERTS: PayrollAlertList = {
    ...MOCK_ALERTS,
    results: [
        {
            ...MOCK_ALERTS.results[0],
            code: 'COMPANY_ONBOARDING_INCOMPLETE',
            resolution: 'wait',
            title: "We're verifying your company",
            message: 'Payroll is on hold while we finish verifying your details. Nothing is needed from you.',
            unblock_options: [],
        },
    ],
};

/**
 * The provider's company review as an alert, which is only written while the payroll notification
 * flags are on. The Overview's own review banner stands in for it.
 *
 * @public Consumed by the Overview tests and stories.
 */
export const COMPANY_IN_REVIEW_ALERTS: PayrollAlertList = {
    ...MOCK_ALERTS,
    results: [
        {
            ...MOCK_ALERTS.results[0],
            code: 'COMPANY_IN_REVIEW',
            resolution: 'wait',
            title: "We're reviewing your company",
            message: "We're reviewing your company details. Payroll can run once the review is done.",
            unblock_options: [],
        },
    ],
};

const MONTHLY_SCHEDULE_UUID = MOCK_OVERVIEW_CORE.next_run?.pay_schedule_uuid ?? 'b7f1b271-0000-4c3a-9b7e-1f2a3b4c5d61';
const BIWEEKLY_SCHEDULE_UUID = 'c2a9e4d0-0000-4c3a-9b7e-1f2a3b4c5d62';

/** The most recent Friday on or before today: the biweekly schedule's anchor. */
const LAST_FRIDAY = TODAY.subtract((TODAY.day() + 2) % 7, 'day');

/**
 * Two schedules, both still carrying Gusto's default names, so the page shows the labels it
 * generates: "Monthly payroll on the last day" and "Every other Friday payroll".
 */
export const MOCK_PAY_SCHEDULES: PayScheduleList = {
    count: 2,
    as_of: null,
    results: [
        {
            pay_schedule_uuid: MONTHLY_SCHEDULE_UUID,
            custom_name: 'every last day of the month',
            name: '',
            frequency: 'monthly',
            day_1: 31,
            day_2: null,
            anchor_pay_date: TODAY.endOf('month').format(ISO_DATE),
            period_end_anchor: TODAY.endOf('month').format(ISO_DATE),
            auto_payroll_enabled: false,
            auto_payroll_blockers: [],
            auto_payroll_blockers_known: true,
            employee_count: 1,
            next_run: MOCK_OVERVIEW_CORE.next_run,
            projected_pay_dates: [],
            projected_pay_dates_known: false,
        },
        {
            pay_schedule_uuid: BIWEEKLY_SCHEDULE_UUID,
            custom_name: 'every other Friday',
            name: '',
            frequency: 'biweekly',
            day_1: null,
            day_2: null,
            anchor_pay_date: LAST_FRIDAY.format(ISO_DATE),
            period_end_anchor: LAST_FRIDAY.subtract(7, 'day').format(ISO_DATE),
            auto_payroll_enabled: false,
            auto_payroll_blockers: [],
            auto_payroll_blockers_known: true,
            employee_count: 1,
            next_run: null,
            projected_pay_dates: [],
            projected_pay_dates_known: false,
        },
    ],
};

/** An unrun biweekly run with no amount yet, as the Late and Upcoming lists show one. */
export const buildPendingRun = (overrides: Partial<PayrollActivityRun> = {}): PayrollActivityRun => ({
    payroll_uuid: 'run',
    pay_schedule_uuid: 'schedule',
    period_start_date: '2026-08-08',
    period_end_date: '2026-08-21',
    check_date: '2026-08-28',
    frequency_label: 'Biweekly payroll',
    total_amount: null,
    date_ran: null,
    date_paid: null,
    display_status: 'UPCOMING',
    can_skip: true,
    can_cancel: false,
    period_conflict: false,
    ...overrides,
});

/** An unrun biweekly run paying `fortnights` pay periods from the anchor; negative is in the past. */
const biweeklyRun = (index: number, fortnights: number, status: 'OVERDUE' | 'UPCOMING') => {
    const payDate = LAST_FRIDAY.add(fortnights * 14, 'day');

    return buildPendingRun({
        payroll_uuid: `d3b8f5e1-000${index}-4c3a-9b7e-1f2a3b4c5d6${index}`,
        pay_schedule_uuid: BIWEEKLY_SCHEDULE_UUID,
        period_start_date: payDate.subtract(20, 'day').format(ISO_DATE),
        period_end_date: payDate.subtract(7, 'day').format(ISO_DATE),
        check_date: payDate.format(ISO_DATE),
        display_status: status,
    });
};

const PENDING_RESULTS = [
    biweeklyRun(1, 3, 'UPCOMING'),
    biweeklyRun(2, 2, 'UPCOMING'),
    biweeklyRun(3, 1, 'UPCOMING'),
    ...MOCK_ACTIVITY.results.filter((run) => run.display_status !== 'UPCOMING'),
    biweeklyRun(4, -1, 'OVERDUE'),
    biweeklyRun(5, -2, 'OVERDUE'),
    biweeklyRun(6, -3, 'OVERDUE'),
];

/**
 * A company with a second, biweekly schedule that has fallen behind: three runs past their pay
 * day and three still to come, so both lists collapse to a stack (Figma "Multiple pay schedules").
 */
export const MOCK_ACTIVITY_WITH_PENDING: PayrollActivityPage = {
    count: PENDING_RESULTS.length,
    as_of: MOCK_ACTIVITY.as_of,
    results: PENDING_RESULTS,
};
